import { c as _c } from "react/compiler-runtime";
import type { ElementType } from 'react';
import MUIButton, { ButtonProps as MUIButtonProps, ButtonTypeMap as MUIButtonTypeMap } from '@mui/material/Button';
import { CSSObject, styled, alpha } from '../styles';
import Loader from '../Loader/Loader';
type Variant = 'primary' | 'secondary' | 'tertiary' | 'destructive';
const MUI_VARIANT: { [key in Variant]: MUIButtonProps['variant'] } = {
  primary: 'contained',
  secondary: 'outlined',
  tertiary: 'text',
  destructive: 'text'
};
const ButtonStyled = styled(MUIButton, {
  shouldForwardProp: prop => !['variantName', 'isOnContrasted', 'isLoading'].includes(prop as string)
})<{
  variantName: Variant;
  isOnContrasted: boolean;
  isLoading?: boolean;
}>(({
  theme: {
    palette,
    spacing,
    shadows,
    radius
  },
  variantName,
  isOnContrasted,
  isLoading
}) => {
  const HOVER_MEDIA_QUERY = '@media (hover: hover) and (pointer: fine)';
  const VARIANTS: {
    [key: string]: {
      default: CSSObject;
      onContrasted?: CSSObject;
    };
  } = {
    primary: {
      default: {
        '&&, &&.Mui-disabled, &&:hover': {
          backgroundColor: palette.interaction.enabled,
          color: palette.interaction.onEnabled
        },
        [HOVER_MEDIA_QUERY]: {
          '&:hover': {
            boxShadow: shadows.containedButtonRaised
          }
        }
      },
      onContrasted: {
        '&&, &&.Mui-disabled, &&:hover': {
          backgroundColor: palette.interaction.onContrasted.enabled,
          color: palette.interaction.onContrasted.onEnabled
        },
        [HOVER_MEDIA_QUERY]: {
          '&:hover': {
            boxShadow: shadows.containedButtonRaised
          }
        }
      }
    },
    secondary: {
      default: {
        '&&, &&.Mui-disabled, &&:hover': {
          color: palette.interaction.enabled,
          padding: `calc(${spacing(1)} - 1px) calc(${spacing(2)} - 1px)`,
          border: `1px solid ${palette.interaction.enabled}`
        },
        [HOVER_MEDIA_QUERY]: {
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.hover, 0.16)
          }
        }
      },
      onContrasted: {
        '&&, &&.Mui-disabled': {
          padding: `calc(${spacing(1)} - 1px) calc(${spacing(2)} - 1px)`,
          color: palette.interaction.onContrasted.enabled,
          border: `1px solid ${palette.interaction.onContrasted.enabled}`
        },
        '&&, &&:hover': {
          color: palette.interaction.onContrasted.enabled,
          borderColor: palette.interaction.onContrasted.enabled
        },
        [HOVER_MEDIA_QUERY]: {
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.onContrasted.enabled, 0.16)
          }
        }
      }
    },
    tertiary: {
      default: {
        '&, &.Mui-disabled': {
          padding: spacing(1)
        },
        '&&, &&.Mui-disabled, &&:hover': {
          color: palette.interaction.enabled
        },
        [HOVER_MEDIA_QUERY]: {
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.hover, 0.16)
          }
        },
        ...(isLoading && {
          '&': {
            backgroundColor: alpha(palette.interaction.hover, 0.16)
          }
        })
      },
      onContrasted: {
        '&, &.Mui-disabled': {
          padding: spacing(1)
        },
        '&&, &&.Mui-disabled, &&:hover': {
          color: palette.interaction.onContrasted.enabled
        },
        [HOVER_MEDIA_QUERY]: {
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.onContrasted.enabled, 0.16)
          }
        },
        ...(isLoading && {
          '&': {
            backgroundColor: alpha(palette.interaction.onContrasted.enabled, 0.16)
          }
        })
      }
    },
    destructive: {
      default: {
        '&, &.Mui-disabled': {
          padding: spacing(1)
        },
        '&&, &&.Mui-disabled, &&:hover': {
          color: palette.interaction.danger
        },
        [HOVER_MEDIA_QUERY]: {
          '&:hover:not(:active)': {
            backgroundColor: alpha(palette.interaction.danger, 0.16)
          }
        },
        ...(isLoading && {
          '&': {
            backgroundColor: alpha(palette.interaction.danger, 0.16)
          }
        })
      }
    }
  };
  return {
    padding: spacing(1, 2),
    borderRadius: radius.interaction,
    '&:disabled': {
      opacity: isOnContrasted ? 0.64 : 0.38
    },
    ...(VARIANTS[variantName][isOnContrasted ? 'onContrasted' : 'default'] || VARIANTS[variantName].default),
    [HOVER_MEDIA_QUERY]: {
      '&&:hover': {
        textDecoration: 'none'
      }
    },
    ...(isLoading ? {
      pointerEvents: 'none',
      opacity: isOnContrasted ? 0.64 : 0.38
    } : {})
  };
});
const Content = styled('span', {
  shouldForwardProp: prop => !['isLoading'].includes(prop as string)
})<{
  isLoading?: boolean;
}>(({
  isLoading
}) => ({
  ...(isLoading ? {
    color: 'transparent'
  } : {})
}));
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    tertiary: true;
    destructive: true;
  }
}
export type Props<D extends ElementType = MUIButtonTypeMap['defaultComponent']> = MUIButtonProps<D, {
  component?: D;
}> & {
  isOnContrasted?: boolean;
  variant?: Variant;
  Component?: never;
  [dataAttribute: `data-${string}`]: any;
  isLoading?: boolean;
  loadingAriaLabel?: string;
};
function Button(t0) {
  const $ = _c(37);
  let children;
  let isLoading;
  let loadingAriaLabel;
  let otherProps;
  let ref;
  let startIcon;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      variant: t1,
      isOnContrasted: t2,
      children,
      sx: t3,
      isLoading,
      loadingAriaLabel,
      startIcon,
      ref,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = isLoading;
    $[3] = loadingAriaLabel;
    $[4] = otherProps;
    $[5] = ref;
    $[6] = startIcon;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    children = $[1];
    isLoading = $[2];
    loadingAriaLabel = $[3];
    otherProps = $[4];
    ref = $[5];
    startIcon = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
  }
  const variant = t1 === undefined ? "primary" : t1;
  const isOnContrasted = t2 === undefined ? false : t2;
  let t4;
  if ($[10] !== t3) {
    t4 = t3 === undefined ? {} : t3;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const sx = t4;
  const variantStyle = MUI_VARIANT[variant];
  let t5;
  if ($[12] !== children) {
    t5 = !children && {
      minWidth: 0,
      ".MuiButton-endIcon, .MuiButton-startIcon": {
        margin: 0
      }
    };
    $[12] = children;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== sx || $[15] !== t5) {
    t6 = {
      ...t5,
      ...sx
    };
    $[14] = sx;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== isLoading) {
    t7 = isLoading ? {
      "aria-disabled": "true",
      onClick: _temp
    } : {};
    $[17] = isLoading;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const t8 = isLoading ? undefined : startIcon;
  let t9;
  if ($[19] !== isLoading || $[20] !== loadingAriaLabel) {
    t9 = isLoading && <Loader size={24} sx={{
      position: "absolute",
      color: "inherit"
    }} aria-label={loadingAriaLabel || ""} />;
    $[19] = isLoading;
    $[20] = loadingAriaLabel;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== children || $[23] !== isLoading) {
    t10 = <Content isLoading={isLoading}>{children}</Content>;
    $[22] = children;
    $[23] = isLoading;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== isLoading || $[26] !== isOnContrasted || $[27] !== otherProps || $[28] !== ref || $[29] !== t10 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8 || $[33] !== t9 || $[34] !== variant || $[35] !== variantStyle) {
    t11 = <ButtonStyled variant={variantStyle} variantName={variant} isOnContrasted={isOnContrasted} isLoading={isLoading} ref={ref} sx={t6} {...otherProps} {...t7} startIcon={t8}>{t9}{t10}</ButtonStyled>;
    $[25] = isLoading;
    $[26] = isOnContrasted;
    $[27] = otherProps;
    $[28] = ref;
    $[29] = t10;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = variant;
    $[35] = variantStyle;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}
function _temp() {}
export default Button;